import { Card } from '@/components/ui';
import { formatGain, improvements } from '@/content/benchmarks';
import type { BenchmarkLevel, GainGroup } from '@/content/benchmarks';

/** One arm against one baseline: each metric and its best relative result. */
export function GainTable({ group }: { group: GainGroup }) {
  const gains = improvements(group.gains);
  if (gains.length === 0) return null;

  return (
    <Card variant="raised" className="overflow-x-auto p-4 sm:p-5">
      <div className="mb-3 grid grid-cols-1 gap-1 border-b border-ink pb-3 sm:grid-cols-2 sm:gap-6">
        <div>
          <p className="label text-accent-ink">{group.arm}</p>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-3">{group.armDetail}</p>
        </div>
        <div>
          <p className="label">vs {group.vs}</p>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-3">{group.vsDetail}</p>
        </div>
      </div>
      <table className="w-full min-w-[320px] border-collapse text-left">
        <tbody>
          {gains.map((g) => (
            <tr key={g.metric} className="border-b border-line last:border-b-0">
              <td className="py-2.5 pr-4 align-top text-[13px] font-medium text-ink">
                {g.metric}
                {g.note && <span className="mt-0.5 block text-[12px] font-normal text-ink-3">{g.note}</span>}
              </td>
              <td className="whitespace-nowrap py-2.5 text-right align-top font-mono text-[13px] font-semibold tabular text-accent-ink">
                {formatGain(g)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/** A benchmark level (direct inference or end to end): every arm's table, then the reading notes. */
export function LevelResults({ level }: { level: BenchmarkLevel }) {
  return (
    <div className="space-y-4">
      {level.groups.map((group) => (
        <GainTable key={`${group.arm}-${group.vs}`} group={group} />
      ))}
      {level.notes?.map((note) => (
        <p key={note} className="max-w-3xl pt-2 text-sm leading-relaxed text-ink-2">
          {note}
        </p>
      ))}
    </div>
  );
}

/** Two-column ruled list — test conditions. */
export function RuledList({ items }: { items: [string, string][] }) {
  return (
    <div className="divide-y divide-line border-y border-line">
      {items.map(([title, detail]) => (
        <div key={title} className="grid grid-cols-1 gap-2 py-4 md:grid-cols-3 md:gap-6">
          <p className="text-sm font-semibold tracking-tight">{title}</p>
          <p className="text-[13px] leading-relaxed text-ink-2 md:col-span-2">{detail}</p>
        </div>
      ))}
    </div>
  );
}
